import { select, put, all } from 'redux-saga/effects';
import {
  setSelectedOpportunityCensusTracts, appNotify, setSelectedProformaInputsContainer, setSelectedOpportunityImages,
  setSelectedOpportunityReloadStatusState, setPropertyUWMarkerState, setTransientValuation, setSelectedValuation,
  setIsOpportunitySfhaDetailLoading, setOpportunitySfhaDetails, setIsFetchingPropertyUWImages, setShouldCalculateProforma
} from '../store/actions';
import {
  userSelector, selectedProformaInputsSelector
} from 'store/selectors';
import { getWithToken, postWithToken } from 'api/network';
import placeholder from 'assets/images/placeholder.png';
import { getGoogleImageUrls, getUWPropertyImage } from 'utilities/urls';
import { fullAddress } from 'utilities/address';
import { ToCurrencyString } from 'services/formatService';
import _ from 'lodash';

const placeholderSource = {
  source: 'Placeholder',
  images: [{
    id: 1,
    caption: null,
    imageUrl: placeholder,
    source: 'Placeholder',
    sortOrder: 1
  }]
}

export function* getProformaInputsAndValuation(proformaInputsContainer, selectedValuationId, transientValuation, callback) {
  const [user] = yield all([
    select(userSelector)
  ]);

  const currentUsersValuation = proformaInputsContainer.valuations.find(v => v.user.id === user.id);
  let selectedValuation;
  // viewValuationDialogProps can only occur in the auction UW screen, if there isn't a selectedValuation, then we default to user where they can enter something in
  if (selectedValuationId != null) {
    selectedValuation = proformaInputsContainer.valuations.find(v => v.id === selectedValuationId);
  } else if (transientValuation == null && proformaInputsContainer.selectedValuationId) { // Transient valuation present when using Valuation History's Take UW, used to set selectedValuation to current user's valuation
    selectedValuation = proformaInputsContainer.valuations.find(v => v.id === proformaInputsContainer.selectedValuationId);
  } else {
    selectedValuation = currentUsersValuation;
  }

  
  yield put(setSelectedProformaInputsContainer(proformaInputsContainer));
  yield put(setSelectedValuation(selectedValuation));
  yield put(setTransientValuation(transientValuation
    ? {
        ...transientValuation,
        id: currentUsersValuation.id // make sure transient has id from the latest container
      }
    : {
        ...currentUsersValuation,
        valuationsComps: [...currentUsersValuation.valuationsComps]
      }
  ));

  let newTransientValuation, newInvalidFields;
  if (transientValuation) {
    const { updatedTransientValuation, invalidFields } = yield removeInvalidProformaInputs(transientValuation);
    newTransientValuation = updatedTransientValuation;
    newInvalidFields = invalidFields;
    yield put(setTransientValuation({
      ...newTransientValuation,
      id: currentUsersValuation.id  // make sure transient has id from the latest container
    }));
    yield put(setShouldCalculateProforma(true));
  }
  
  yield put(setPropertyUWMarkerState({
    selectedMarker: null,
    isInfoBoxOpen: false,
    isFetchingImages: false,
    selectedMarkerImages: [],
    uncapturedMarkers: []
  }));
  if (callback) {
    yield callback(newInvalidFields);
  }
}

export function* removeInvalidProformaInputs(transientValuation) {
  const selectedProformaInputs = yield select(selectedProformaInputsSelector);
  const inputGroups = {
    hoa: {
      name:'hoas',
      displayName: 'HOA'
    },
    repairEstimate: {
      name:'repairEstimates',
      displayName: 'Repair'
    },
    rent: {
      name:'rents',
      displayName: 'Rent'
    },
    afterRepairValue: {
      name:'afterRepairValues',
      displayName: 'After Repair Value'
    },
    closingCosts: {
      name:'closingCosts',
      displayName: 'Closing Costs'
    },
    bidPrice: {
      name:'bidPrices',
      displayName: 'Bid Price'
    },
    taxes: {
      name:'taxes',
      displayName: 'Taxes'
    }
  };

  const compareInputValueToRadioValue = (inputValue, radioValue) => {
    // Both values are allowed to be null
    // If both are null or undefined, then we consider they the same
    if (inputValue == null && radioValue == null) return true;
    // Since all inputs are dollar amoutns, if both have value, we compare the value by rounding them to 0 decimals
    // NOTE: We do this to avoid any discrepancy between saved/calculated decimal numbers 
    if (inputValue != null && radioValue != null) return _.round(inputValue, 0) === _.round(radioValue, 0)
    
    // If one has value whereas the other doesn't, apparently they are different
    return false;
  }

  let invalidFields = '';
  let isFirst = true;
  const updatedTransientValuation = {
    ...transientValuation,
    valuationInput: {
      ...transientValuation.valuationInput
    }
  }
  Object.keys(inputGroups).forEach(group => {
    const inputValue = transientValuation.valuationInput[group];
    const inputValueType = transientValuation.valuationInput[group + 'Type'];
    if (inputValueType === 0 || inputValueType == null) return;

    const proformaOption = selectedProformaInputs[inputGroups[group].name].find(opt => opt.type === inputValueType);
    if (!proformaOption || !compareInputValueToRadioValue(inputValue, proformaOption.value)) {
      updatedTransientValuation.valuationInput[group] = null;
      updatedTransientValuation.valuationInput[group+ 'Type'] = null;
      if (isFirst) {
        invalidFields += '\n\nIncompatible inputs:';
        isFirst = false;
      }
      invalidFields += `\n${inputGroups[group].displayName}, ${inputValue != null ? ToCurrencyString(inputValue) : inputValue}`;
    }
  });
  return { updatedTransientValuation, invalidFields };
}

export function* getPropertyUWCensusTract(taskId) {
  try {
    const response = yield getWithToken(`/api/underwrite/prop/markets/${taskId}`);      
    const fetchedData = yield response.json();
    yield put(setSelectedOpportunityCensusTracts(fetchedData));
  }
  catch (error) {
    yield put(appNotify(error.message, 'error'));
  }
}

export function* getPropertyUWImages(fetchedOpp) {
  if (!fetchedOpp) {
    yield put(setSelectedOpportunityImages([placeholderSource]));
    return;
  }

  const getGoogleImageSource = (googleImageUrls) => {
		const images = [];
		if (googleImageUrls.streetViewUrl) {
			images.push({
				id: 1,
				caption: 'Street View',
				imageUrl: googleImageUrls.streetViewUrl,
				source: 'Google',
				sortOrder: 1
			});
		}
		images.push({
			id: images.length,
			caption: 'Satellite View',
			imageUrl: googleImageUrls.mapsStaticUrl,
			source: 'Google',
			sortOrder: images.length
		})
		const googleImageSource = {
			source: 'Google',
			images
		}
		return googleImageSource;
	};

  yield put(setIsFetchingPropertyUWImages(true));
  yield put(setSelectedOpportunityImages([placeholderSource]));
  try {
    const [googleImageUrls, msrImage] = yield all([
      getGoogleImageUrls(fullAddress(fetchedOpp), 640, 360),        
      getUWPropertyImage(fetchedOpp.asgPropID, fetchedOpp.id, 'PropertyUWMain')
    ]);

    let propertyImages = [];
    if (msrImage) {
      propertyImages = [...msrImage]
    }
    propertyImages.push(getGoogleImageSource(googleImageUrls));
    if (propertyImages.length) { // If property has images, add to cache and update selectedOpportunityImages, else leave placeholder.
      yield put(setSelectedOpportunityImages(propertyImages));
    }
    yield put(setIsFetchingPropertyUWImages(false));
  }
  catch (error) {
    yield put(appNotify(error.message, 'error'));
    yield put(setIsFetchingPropertyUWImages(false));
  }
}

export function* checkPropertyUWReloadStatus(taskId) {
  try {
    yield put(setSelectedOpportunityReloadStatusState({ isLoading: true, needsRefresh: false, message: null }));
    const response = yield postWithToken(`/api/underwrite/workflow/opportunity/reloadStatus`, [taskId]);
    const fetchedData = yield response.json();
    yield put(setSelectedOpportunityReloadStatusState({
      isLoading: false,
      data: fetchedData
    }));
  }
  catch (error) {
    yield put(appNotify(error.message, 'error'));
    yield put(setSelectedOpportunityReloadStatusState({ isLoading: false, data: null, message: 'Unable to check reload status' }));
  }
}

export function* getOpportunitySfhaDetailsSaga(txnOppId) {
  try {
    yield put(setIsOpportunitySfhaDetailLoading(true));

    const url = `/api/underwrite/prop/${txnOppId}/sfhaDetails`;
    const response = yield getWithToken(url);
    const data = yield response.json();

    yield put(setOpportunitySfhaDetails(data));
    yield put(setIsOpportunitySfhaDetailLoading(false));
  } catch (e) {
      yield put(setOpportunitySfhaDetails(e));
      yield put(setIsOpportunitySfhaDetailLoading(false));
  }
}